import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { KpiGrid } from '../../components/ui/KpiCard';
import { YoolTable, StatusPill, StackedCell, RowActions, IconButton } from '../../components/ui/YoolTable';
import { Download, RefreshCw, Users, DollarSign, Calendar, TrendingUp } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { SubscriptionsService } from '../../services/subscriptionsService';
import virtualPosService from '../../services/virtualPosService';

const SubscriptionsList = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const { toast } = useToast();

  const mapVirtualPosStatus = (virtualPosStatus) => {
    const status = String(virtualPosStatus || '').toUpperCase();
    if (status === 'ACTIVA') return 'active';
    if (status === 'SUSCRIBIENDO') return 'paused';
    if (status === 'CANCELADA') return 'cancelled';
    if (status === 'FINALIZADA' || status === 'EXPIRADA' || status === 'SUSCRIPCION_FALLIDA') return 'expired';
    return 'active';
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);

      // Usar VirtualPos en lugar de FileMaker
      const subscriptionsResponse = await virtualPosService.getSubscriptions({});
      const virtualPosSubscriptions = subscriptionsResponse?.suscriptions || [];

      // Mapear datos de VirtualPos al formato esperado
      const mappedSubscriptions = virtualPosSubscriptions.map(sub => ({
        id: sub.id,
        clientName: `${sub.client?.first_name || ''} ${sub.client?.last_name || ''}`.trim(),
        clientEmail: sub.client?.email,
        planName: sub.plan_name,
        status: mapVirtualPosStatus(sub.status),
        nextPayment: sub.charge_program?.[0]?.charge_date || sub.charges_program?.[0]?.charge_date,
        amount: sub.amount,
        startDate: sub.suscription_date,
        paymentMethod: sub.payment_method?.brand ? `${sub.payment_method?.brand} ****${sub.payment_method?.last4CardDigit || ''}` : '',
        created_at: sub.suscription_date
      }));

      setSubscriptions(mappedSubscriptions);

      // Calcular estadísticas basadas en los datos de VirtualPos
      const activeSubscriptions = mappedSubscriptions.filter(s => s.status === 'active').length;
      const pausedSubscriptions = mappedSubscriptions.filter(s => s.status === 'paused').length;
      const cancelledSubscriptions = mappedSubscriptions.filter(s => s.status === 'cancelled').length;

      const totalRevenue = mappedSubscriptions
        .filter(s => s.status === 'active')
        .reduce((sum, sub) => sum + (parseFloat(sub.amount) || 0), 0);

      setStats({
        totalSubscriptions: mappedSubscriptions.length,
        activeSubscriptions,
        pausedSubscriptions,
        cancelledSubscriptions,
        totalRevenue,
        retentionRate: mappedSubscriptions.length > 0 ? ((activeSubscriptions / mappedSubscriptions.length) * 100).toFixed(1) : 0
      });
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar las suscripciones',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CO');
  };

  const handleSubscriptionAction = async (subscriptionId, action) => {
    try {
      const updateData = {};

      if (action === 'pause') {
        updateData.estado = 'paused';
      } else if (action === 'resume') {
        updateData.estado = 'active';
      } else if (action === 'cancel') {
        updateData.estado = 'cancelled';
        updateData.fecha_cancelacion = new Date().toISOString();
      }

      await SubscriptionsService.updateSubscription(subscriptionId, updateData);

      toast({
        title: 'Éxito',
        description: `Suscripción ${action === 'pause' ? 'pausada' : action === 'resume' ? 'reactivada' : 'cancelada'} correctamente`
      });

      fetchSubscriptions();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        title: 'Error',
        description: 'Error al actualizar la suscripción',
        variant: 'destructive'
      });
    }
  };

  const columns = [
    {
      key: 'clientName',
      label: 'Cliente',
      render: (r) => <StackedCell primary={r.clientName} secondary={r.clientEmail} />,
    },
    {
      key: 'planName',
      label: 'Plan',
    },
    {
      key: 'status',
      label: 'Estado',
      render: (r) => <StatusPill estado={r.status} />,
    },
    {
      key: 'amount',
      label: 'Monto',
      numeric: true,
      sortable: true,
      sortValue: (r) => parseFloat(r.amount) || 0,
      render: (r) => formatCurrency(r.amount),
    },
    {
      key: 'nextPayment',
      label: 'Próximo Pago',
      render: (r) => formatDate(r.nextPayment),
    },
    {
      key: 'paymentMethod',
      label: 'Método de Pago',
    },
    {
      key: 'acciones',
      label: '',
      align: 'right',
      render: (r) => (
        <RowActions>
          <IconButton icon="eye" title="Ver" onClick={() => {}} />
          {r.status === 'active' && (
            <IconButton icon="pause" title="Pausar" onClick={() => handleSubscriptionAction(r.id, 'pause')} />
          )}
          {r.status === 'paused' && (
            <IconButton icon="refresh" title="Reactivar" onClick={() => handleSubscriptionAction(r.id, 'resume')} />
          )}
          {(r.status === 'active' || r.status === 'paused') && (
            <IconButton icon="trash" title="Cancelar" danger onClick={() => handleSubscriptionAction(r.id, 'cancel')} />
          )}
        </RowActions>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Suscripciones</h1>
        <div className="flex gap-2">
          <Button onClick={fetchSubscriptions} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Estadísticas de Suscripciones */}
      {stats && (
        <KpiGrid
          cards={[
            {
              title: "Total Suscripciones",
              value: stats.totalSubscriptions,
              description: "Suscripciones registradas",
              icon: Users,
              trend: "neutral",
              color: "blue"
            },
            {
              title: "Suscripciones Activas",
              value: stats.activeSubscriptions,
              description: `${stats.retentionRate}% retención`,
              icon: TrendingUp,
              trend: "up",
              color: "green"
            },
            {
              title: "Ingresos Mensuales",
              value: `$${stats.totalRevenue.toLocaleString()}`,
              description: "De suscripciones activas",
              icon: DollarSign,
              trend: "up",
              color: "green"
            },
            {
              title: "Suscripciones Pausadas",
              value: stats.pausedSubscriptions,
              description: `${stats.cancelledSubscriptions} canceladas`,
              icon: Calendar,
              trend: stats.pausedSubscriptions > 0 ? "down" : "neutral",
              color: stats.pausedSubscriptions > 0 ? "yellow" : "gray"
            }
          ]}
        />
      )}

      {loading ? (
        <div className="text-center py-8">Cargando suscripciones...</div>
      ) : (
        <YoolTable
          columns={columns}
          rows={subscriptions}
          rowKey="id"
          search={{ placeholder: 'Buscar por cliente o email…', keys: ['clientName', 'clientEmail', 'planName'] }}
          filters={[
            {
              key: 'status',
              label: 'Estado',
              options: [
                { value: 'active', label: 'Activa' },
                { value: 'paused', label: 'Pausada' },
                { value: 'cancelled', label: 'Cancelada' },
              ],
            },
          ]}
          toolbar={
            <Button onClick={fetchSubscriptions} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          }
        />
      )}
    </div>
  );
};

export default SubscriptionsList;
